import { useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { QUOTES, pick, shuffle } from "@/data/quotes";
import { SCRAMBLE_LEVELS, loadStars, saveStars } from "@/lib/game-utils";

const ROUNDS_PER_LEVEL = 5;

interface Round { quoteId: string; words: string[]; }

function buildRounds(level: number): Round[] {
  const cfg = SCRAMBLE_LEVELS.find((l) => l.id === level) ?? SCRAMBLE_LEVELS[0];
  const pool = QUOTES.filter((q) => {
    const wc = q.text.split(/\s+/).length;
    return wc >= cfg.min && wc <= cfg.max;
  });
  const source = pool.length >= ROUNDS_PER_LEVEL ? pool : QUOTES;
  return pick(source, ROUNDS_PER_LEVEL).map((q) => ({ quoteId: q.id, words: q.text.split(/\s+/) }));
}

export function ScrambleGame({ level = 1 }: { level?: number }) {
  const live = useLiveMode();
  const cfg = SCRAMBLE_LEVELS.find((l) => l.id === level) ?? SCRAMBLE_LEVELS[0];
  const [rounds, setRounds] = useState<Round[]>(() => buildRounds(level));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [resets, setResets] = useState(0);
  const [timedOut, setTimedOut] = useState(0);
  const [done, setDone] = useState(false);
  const [stars, setStars] = useState(0);

  useEffect(() => { setStars(loadStars(level)); }, [level]);
  useEffect(() => {
    setRounds(buildRounds(level));
    setIdx(0); setScore(0); setResets(0); setTimedOut(0); setDone(false);
  }, [level]);

  const round = rounds[idx];
  const quote = useMemo(() => QUOTES.find((q) => q.id === round?.quoteId)!, [round]);
  const initialPool = useMemo(() => shuffle(round.words.map((w, i) => ({ w, key: i }))), [round]);
  const [pool, setPool] = useState(initialPool);
  const [chosen, setChosen] = useState<{ w: string; key: number }[]>([]);
  const [verdict, setVerdict] = useState<"none" | "right" | "wrong">("none");

  useEffect(() => { setPool(initialPool); setChosen([]); setVerdict("none"); }, [initialPool]);

  const pickWord = (k: number) => {
    if (verdict !== "none") return;
    const item = pool.find((p) => p.key === k);
    if (!item) return;
    setPool(pool.filter((p) => p.key !== k));
    setChosen([...chosen, item]);
  };
  const unpick = (k: number) => {
    if (verdict !== "none") return;
    const item = chosen.find((p) => p.key === k);
    if (!item) return;
    setChosen(chosen.filter((p) => p.key !== k));
    setPool([...pool, item]);
  };

  const submit = () => {
    if (chosen.length !== round.words.length) return;
    const guess = chosen.map((c) => c.w).join(" ");
    if (guess === round.words.join(" ")) { setScore((s) => s + 1); setVerdict("right"); }
    else setVerdict("wrong");
  };

  const next = () => {
    if (idx + 1 >= rounds.length) {
      // Award stars: 1 = complete, 2 = clean (no resets), 3 = clean + no timeouts
      const finalScore = score;
      let earned = 1;
      if (finalScore >= ROUNDS_PER_LEVEL - 1 && resets === 0) earned = 3;
      else if (finalScore >= Math.ceil(ROUNDS_PER_LEVEL * 0.6) && resets <= 1) earned = 2;
      setStars(saveStars(level, earned));
      setDone(true);
    } else {
      setIdx(idx + 1);
    }
  };

  const restart = () => {
    setRounds(buildRounds(level));
    setIdx(0); setScore(0); setResets(0); setTimedOut(0); setDone(false);
  };

  if (done) {
    return (
      <FinishScreen
        score={score}
        total={ROUNDS_PER_LEVEL}
        best={stars}
        bestStreak={undefined}
        onRestart={restart}
        title={`Quote Scramble · ${cfg.name}`}
        numeral="III"
        hubSlug="scramble"
      />
    );
  }

  return (
    <GameShell
      title={`Scramble · ${cfg.name}`}
      numeral="III"
      score={score}
      round={idx + 1}
      total={ROUNDS_PER_LEVEL}
      timeLimit={cfg.timeLimit}
      timeKey={`${level}-${idx}`}
      onTimeUp={() => { if (verdict === "none") { setVerdict("wrong"); setTimedOut((t) => t + 1); } }}
      hubSlug="scramble"
    >
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 min-h-[140px] shadow-elegant animate-reveal">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Your quote</div>
        <div className={`flex flex-wrap gap-2 ${live ? "text-2xl" : "text-lg"}`}>
          {chosen.length === 0 && <span className="text-muted-foreground italic">Tap the words below to build the quote…</span>}
          {chosen.map((c) => (
            <button key={c.key} onClick={() => unpick(c.key)} disabled={verdict !== "none"}
              className="rounded-lg border border-gold/40 bg-gold/10 px-3 py-1.5 font-display hover:bg-gold/20 transition">{c.w}</button>
          ))}
        </div>
        <div className="mt-4 text-sm text-muted-foreground">— {quote.author}{quote.era ? ` · ${quote.era}` : ""}</div>
      </div>

      <div className="mt-6 rounded-2xl border border-dashed border-border p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Available words</div>
        <div className={`flex flex-wrap gap-2 ${live ? "text-xl" : ""}`}>
          {pool.map((p) => (
            <button key={p.key} onClick={() => pickWord(p.key)} disabled={verdict !== "none"}
              className="rounded-lg border border-border bg-secondary px-3 py-1.5 font-display hover:border-gold hover:bg-accent transition">{p.w}</button>
          ))}
          {pool.length === 0 && <span className="text-muted-foreground text-sm italic">All words placed.</span>}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
        {verdict === "none" ? (
          <>
            <button
              onClick={() => { setPool([...initialPool]); setChosen([]); setResets((r) => r + 1); }}
              className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >Reset</button>
            <button onClick={submit} disabled={chosen.length !== round.words.length}
              className="rounded-full bg-gradient-gold px-6 py-2 text-sm font-semibold text-ink disabled:opacity-40 disabled:cursor-not-allowed">Submit</button>
          </>
        ) : (
          <>
            <div className="text-sm">
              {verdict === "right" ? <span className="text-success font-medium">✓ Perfectly rebuilt.</span> : <><span className="text-error font-medium">✗ Real line: </span><span className="text-gold font-display">{quote.text}</span></>}
            </div>
            <button onClick={next} className="rounded-full bg-gradient-gold px-5 py-2 text-sm font-semibold text-ink">{idx + 1 >= rounds.length ? "See results" : "Next →"}</button>
          </>
        )}
      </div>
    </GameShell>
  );
}
